<template>
  <!-- 공개(anon) 가격 안내 페이지. Paddle 온보딩 제출용 + 일반 공개. 로그인/사이드바 없음. -->
  <div class="pricing-root">
    <div class="pricing-content">
      <!-- 언어(통화) 선택: ko→₩ / en→$ / ja→¥ -->
      <div class="lang-switch">
        <button
          v-for="opt in localeOptions"
          :key="opt.code"
          class="lang-switch__btn"
          :class="{ active: $i18n.locale === opt.code }"
          @click="changeLocale(opt.code)"
        >
          {{ opt.label }}
        </button>
      </div>

      <header class="pricing-head">
        <div class="d-flex align-center justify-center mb-2">
          <img src="@/assets/login/icon.webp" width="36" height="36" class="mr-2" alt="virtupet" />
          <h1 class="brand">버츄펫 (Virtupet)</h1>
        </div>
        <h2 class="pricing-title">{{ $t("pricing.title") }}</h2>
        <p class="pricing-sub">{{ $t("pricing.subtitle") }}</p>
        <!-- 재화 성격 고지: 환급·제3자 지급·후원 아님 (결제사 온보딩 요건 겸 사용자 고지) -->
        <p class="pricing-disclaimer">{{ $t("pricing.disclaimer") }}</p>
      </header>

      <div class="pkg-grid">
        <div v-for="pkg in packages" :key="pkg.carrots" class="pkg-card">
          <div class="pkg-card__emoji">🥕</div>
          <div class="pkg-card__amount">
            {{ $t("pricing.carrots", { n: pkg.carrots.toLocaleString($i18n.locale) }) }}
          </div>
          <div v-if="pkg.bonus" class="pkg-card__bonus">
            {{ $t("pricing.bonus", { n: pkg.bonus.toLocaleString($i18n.locale) }) }}
          </div>
          <div class="pkg-card__price">{{ priceLabel(pkg) }}</div>
        </div>
      </div>

      <ul class="pricing-notes">
        <li>{{ $t("pricing.note1") }}</li>
        <li>{{ $t("pricing.note2") }}</li>
        <li>{{ $t("pricing.note3") }}</li>
        <li>
          {{ $t("pricing.contact") }} <a :href="`mailto:${contactEmail}`">{{ contactEmail }}</a>
        </li>
      </ul>
    </div>

    <TheFooter class="pricing-footer" />
  </div>
</template>

<script>
import TheFooter from "@/components/TheFooter.vue";
import { CARROT_PACKAGES, currencyForLocale, formatPrice } from "@/constants/currencyPackages";
import { setLocale } from "@/plugins/i18n";

const CONTACT_EMAIL = "virtupet.official@gmail.com";

export default {
  name: "PricingPage",
  components: { TheFooter },
  data() {
    return {
      packages: CARROT_PACKAGES,
      contactEmail: CONTACT_EMAIL,
      localeOptions: [
        { code: "ko", label: "한국어 ₩" },
        { code: "en", label: "English $" },
        { code: "ja", label: "日本語 ¥" },
      ],
    };
  },
  computed: {
    currency() {
      return currencyForLocale(this.$i18n.locale);
    },
  },
  methods: {
    changeLocale(code) {
      setLocale(code);
      this.$vuetify.lang.current = code;
    },
    priceLabel(pkg) {
      const amount = pkg.price[this.currency];
      return formatPrice(amount, this.currency, this.$i18n.locale);
    },
  },
};
</script>

<style scoped>
.pricing-root {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: #f4f5fa;
}
.pricing-content {
  flex: 1 0 auto;
  max-width: 920px;
  margin: 0 auto;
  padding: 32px 20px 24px;
  text-align: center;
  position: relative;
  width: 100%;
}
.lang-switch {
  display: flex;
  justify-content: flex-end;
  gap: 6px;
  margin-bottom: 12px;
}
.lang-switch__btn {
  font-size: 13px;
  padding: 6px 12px;
  border-radius: 999px;
  border: 1px solid #ddd9e8;
  background: #fff;
  color: #6b6776;
  cursor: pointer;
  transition: all 0.15s;
}
.lang-switch__btn.active {
  background: #9155fd;
  border-color: #9155fd;
  color: #fff;
  font-weight: 700;
}
.brand {
  font-size: 26px;
  font-weight: 800;
}
.pricing-title {
  margin-top: 4px;
  font-size: 20px;
  color: #57545d;
}
.pricing-disclaimer {
  max-width: 640px;
  margin: 10px auto 0;
  font-size: 13px;
  line-height: 1.6;
  color: #8b8b9e;
}
.pricing-sub {
  max-width: 640px;
  margin: 12px auto 0;
  color: #6b6776;
  font-size: 14px;
  line-height: 1.6;
}
.pkg-grid {
  margin-top: 36px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  gap: 16px;
}
.pkg-card {
  background: #fff;
  border: 1px solid #ece9f3;
  border-radius: 16px;
  padding: 26px 18px 22px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}
.pkg-card__emoji {
  font-size: 40px;
}
.pkg-card__amount {
  margin-top: 6px;
  font-size: 18px;
  font-weight: 800;
  color: #2a2540;
}
.pkg-card__bonus {
  font-size: 12px;
  font-weight: 700;
  color: #e0892b;
}
.pkg-card__price {
  margin-top: 10px;
  font-size: 24px;
  font-weight: 800;
  color: #9155fd;
}
.pkg-card__tax {
  font-size: 11px;
  color: #9a96a8;
}
.pricing-notes {
  max-width: 640px;
  margin: 36px auto 0;
  text-align: left;
  color: #6b6776;
  font-size: 13px;
  line-height: 1.8;
}
.pricing-footer {
  flex-shrink: 0;
  padding: 24px 16px;
}
</style>
